import { Button, Spinner } from '@heroui/react'
import useOtpInputs from '@/hooks/useOtpInputs'
import PublicHeader from '@/components/public/Header'

export default function OtpForm({
	email,
	otp,
	setOtp,
	onVerify,
	sending,
	error,
	cooldown,
	onResend,
	showStartOver = false,
	onBack,
}: {
	email: string
	otp: string[]
	setOtp: (otp: string[]) => void
	onVerify: (code: string) => void
	sending: boolean
	error?: string | null
	cooldown: number
	onResend: () => Promise<void>
	showStartOver?: boolean
	onBack: () => void
}) {
	const { refs, handleOtpChange, handleOtpPaste, handleKeyDown, focusFirstWithRetries, clearOtp } =
		useOtpInputs(otp, setOtp, onVerify)

	return (
		<div className="flex flex-col items-center justify-center bg-black">
			<PublicHeader description="Begin or resume your application" />
			<section className="text-foreground-500 flex w-full max-w-lg flex-col gap-3">
				<div>
					<p>
						We sent a verification code to <span className="font-bold text-white">{email}</span>.
					</p>
					<p className="mt-4">
						If no account is found, you will start a new application. If one exists, you will
						continue your saved application.
					</p>
				</div>

				<div className="my-5 flex justify-center gap-2">
					{otp.map((digit, i) => (
						<input
							key={i}
							type="text"
							maxLength={1}
							className="rounded-medium border-medium border-default-200 relative flex h-20 w-12 items-center justify-center bg-black text-center text-4xl shadow-sm transition-all duration-300 outline-none focus:border-white focus:caret-white sm:w-14"
							value={digit}
							ref={(el) => {
								refs.current[i] = el
							}}
							disabled={sending}
							onChange={(e) => handleOtpChange(e.target.value, i)}
							onPaste={(e) => handleOtpPaste(e, i)}
							onKeyDown={(e) => handleKeyDown(e, i)}
						/>
					))}
				</div>
				<div className="flex w-full flex-col items-center gap-3">
					{!sending && error && <p className="text-danger mb-2 text-center text-sm">{error}</p>}

					{cooldown > 0 && (
						<p className="text-foreground-400 text-sm">
							Send a new code in{' '}
							<span className="font-mono tabular-nums">
								{String(Math.floor(cooldown / 60)).padStart(2, '0')}:
								{String(cooldown % 60).padStart(2, '0')}
							</span>
						</p>
					)}
					<Button
						variant="solid"
						size="sm"
						isDisabled={cooldown > 0 || sending}
						onPress={async () => {
							if (cooldown > 0 || sending) return
							try {
								// Await server resend; parent will toggle sending state
								await onResend()
								// Clear inputs after successful resend so we don't race with parent
								clearOtp()
								// Focus the first input; retries handle brief timing issues
								focusFirstWithRetries()
							} catch {
								// letting parent handle errors/display; still attempt to focus
								focusFirstWithRetries()
							}
						}}
					>
						Resend code
					</Button>
					{sending && (
						<div className="mt-2 flex justify-center">
							<Spinner size="sm" />
						</div>
					)}
					{!sending && showStartOver && (
						<Button variant="flat" onPress={onBack} className="w-full" isDisabled={sending}>
							Click here to start over
						</Button>
					)}
				</div>
			</section>
		</div>
	)
}
